import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, ChevronDown, Factory, Loader2, Info, Search, BarChart3 } from 'lucide-react'
import { Card } from '../../../components/ui/Card'
import { DashboardViewer } from '../../../components/DashboardViewer'
import { api } from '../../../lib/api'
import { useLanguage } from '../../../context/LanguageContext'
import { usePermissions } from '../../../hooks/usePermissions'
import { useFactory } from '../../../context/FactoryContext'

// Mapeo de códigos del Nivel 3 a sus UIDs de dashboard
const DASHBOARD_UIDS = {
  '0_Common': '8af4860c-b9ff-4348-bd7f-be5dc07b0e94',
  '1_Transport': '6cb7e678-82e6-405b-8680-cc2bb4718334',
  '2_Dosing': '93db2837-1ca0-4c0c-9e5f-33c02d7fe9cd',
  '3_Extrusion': '3a382923-f9ad-42c6-bea9-e34f6c9d8fc7',
  '5_Stack': 'd83dee44-37c2-465b-b9de-81285c928756',
  '8_Winders': '468dac3c-f23e-4d4e-9391-e506bc5a5a5f',
}

function ParametrosProcesos() {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const { permissions, checkFactoryAccess } = usePermissions()
  const { selectedFactory } = useFactory()

  const [hierarchies, setHierarchies] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedNodes, setExpandedNodes] = useState(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024)
  
  // Estados para niveles y variables
  const [selectedNode, setSelectedNode] = useState(null)
  const [selectedLevel, setSelectedLevel] = useState(null)
  const [factory, setFactory] = useState(null)
  const [prodLine, setProdLine] = useState(null)
  const [dashboardFactory, setDashboardFactory] = useState(null) // Factory que se pasa al dashboard (puede ser diferente)
  const [dashboardUid, setDashboardUid] = useState('6cb7e678-82e6-405b-8680-cc2bb4718334')
  const [nodePath, setNodePath] = useState([]) // Ruta completa del nodo seleccionado
  const [extruder, setExtruder] = useState(null)
  const [machine, setMachine] = useState(null)
  // Hook para responsive
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024)
    }
    
    window.addEventListener('resize', handleResize)
    handleResize()
    
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setSelectedNode(null)
    setSelectedLevel(null)
    setFactory(null)
    setProdLine(null)
    setDashboardFactory(null)
    setNodePath([])
    setExpandedNodes(new Set())
    setSearchQuery('')
    loadHierarchies()
  }, [selectedFactory])

  // Construir jerarquía desde la lista de assets usando ParentID
  const buildHierarchy = (assets) => {
    const hierarchies = {}
    
    if (!assets || !Array.isArray(assets) || assets.length === 0) {
      console.warn('buildHierarchy: No assets provided or empty array')
      return hierarchies
    }
    
    // Normalizar campos (soporta mayúsculas y minúsculas)
    const normalizeAsset = (asset) => {
      return {
        id: asset.ID !== undefined ? asset.ID : asset.id,
        parentId: asset.ParentID !== undefined ? asset.ParentID : asset.ParentId !== undefined ? asset.ParentId : asset.parentId !== undefined ? asset.parentId : asset.parent_id,
        code: asset.Code !== undefined ? asset.Code : asset.code,
        name: asset.Name !== undefined ? asset.Name : asset.name,
        location: asset.Location !== undefined ? asset.Location : asset.location,
        factory: asset.Factory !== undefined ? asset.Factory : asset.factory,
        ...asset // Mantener todos los campos originales
      }
    }
    
    // Normalizar todos los assets
    const normalizedAssets = assets.map(normalizeAsset)
    
    // Crear mapa de assets por ID para acceso rápido
    const assetMap = new Map()
    normalizedAssets.forEach(asset => {
      if (asset.id != null) {
        assetMap.set(asset.id, { ...asset, children: [] })
      }
    })
    
    // Identificar nodos raíz (sin padre o ParentID === null)
    const rootNodes = []
    normalizedAssets.forEach(asset => {
      if (asset.id != null && (asset.parentId == null || asset.parentId === null || asset.parentId === '')) {
        rootNodes.push(asset)
      }
    })
    
    // Función recursiva para construir el árbol
    const buildTree = (asset) => {
      const node = assetMap.get(asset.id)
      if (!node) return null
      
      // Buscar todos los hijos de este nodo
      const children = normalizedAssets
        .filter(child => {
          const childParentId = child.parentId
          return childParentId != null && childParentId === asset.id
        })
        .map(child => buildTree(child))
        .filter(Boolean) // Remover nulls
      
      if (children.length > 0) {
        node.children = children
      }
      
      return node
    }
    
    // Construir árboles para cada nodo raíz
    rootNodes.forEach(rootAsset => {
      const tree = buildTree(rootAsset)
      if (tree) {
        // Usar el código del root como clave de fábrica
        const factoryCode = rootAsset.code || rootAsset.factory || `ROOT_${rootAsset.id}`
        hierarchies[factoryCode] = tree
      }
    })
    
    // Ordenar children en cada nivel recursivamente
    const sortChildren = (node) => {
      if (node.children && node.children.length > 0) {
        node.children.sort((a, b) => {
          const aCode = (a.code || '').toString()
          const bCode = (b.code || '').toString()
          return aCode.localeCompare(bCode, undefined, { numeric: true, sensitivity: 'base' })
        })
        node.children.forEach(sortChildren)
      }
    }
    
    Object.values(hierarchies).forEach(sortChildren)
    
    return hierarchies
  }

  const loadHierarchies = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Verificar autenticación antes de hacer la petición
      try {
        const authCheck = await fetch('/api/auth/check', {
          credentials: 'include'
        })
        if (!authCheck.ok) {
          console.warn('Sesión no válida, redirigiendo a login...')
          window.location.href = '/login'
          return
        }
      } catch (authError) {
        console.error('Error verificando autenticación:', authError)
        window.location.href = '/login'
        return
      }
      
      // Usar /api/asset/list y construir la jerarquía en el cliente
      const response = await api.get('/api/asset/list')
      
      // La respuesta podría ser un array directo o un objeto con datos
      let assets = []
      if (Array.isArray(response)) {
        assets = response
      } else if (response && Array.isArray(response.data)) {
        assets = response.data
      } else if (response && response.assets && Array.isArray(response.assets)) {
        assets = response.assets
      } else if (response && typeof response === 'object') {
        // Intentar extraer cualquier array del objeto
        const keys = Object.keys(response)
        const arrayKey = keys.find(key => Array.isArray(response[key]))
        if (arrayKey) {
          assets = response[arrayKey]
        }
      }
      
      if (assets.length === 0) {
        console.warn('No assets found in response')
        setHierarchies({})
        setLoading(false)
        return
      }
      
      // Construir jerarquía desde los assets
      const hierarchies = buildHierarchy(assets)
      
      if (selectedFactory && hierarchies[selectedFactory]) {
        setHierarchies({ [selectedFactory]: hierarchies[selectedFactory] })
      } else {
        setHierarchies(hierarchies)
      }
      
    } catch (err) {
      console.error('Error loading hierarchies:', err)
      setError('Error de conexión: ' + (err.message || 'Desconocido'))
    } finally {
      setLoading(false)
    }
  }

  // Función para encontrar la ruta completa de un nodo hasta la raíz
  const findNodePath = (nodeId, hierarchies) => {
    const findPathRecursive = (currentNode, targetId, path = []) => {
      if (currentNode.id === targetId) {
        return [...path, currentNode]
      }
      
      if (currentNode.children && currentNode.children.length > 0) {
        for (const child of currentNode.children) {
          const result = findPathRecursive(child, targetId, [...path, currentNode])
          if (result) {
            return result
          }
        }
      }
      
      return null
    }
    
    // Buscar en todas las jerarquías
    for (const factoryTree of Object.values(hierarchies)) {
      const path = findPathRecursive(factoryTree, nodeId)
      if (path) {
        return path
      }
    }
    
    return []
  }

  // Función para determinar nivel y capturar variables
  const handleNodeSelect = (node) => {
    setSelectedNode(node)
    
    // Encontrar la ruta completa del nodo
    const path = findNodePath(node.id, hierarchies)
    setNodePath(path)
    
    const level = path.length - 1 // -1 porque el path incluye el nodo mismo
    
    if (level === 0) {
      // Nivel 0: Fábrica
      setFactory(node.code)
      setProdLine(null)
      setSelectedLevel(0)
    } else if (level === 1) {
      // Nivel 1: Línea - Mostrar dashboard de línea
      const factoryNode = path[0]
      const lineNode = node
      setFactory(factoryNode.code)
      setProdLine(lineNode.code)
      setDashboardUid('ceh0v6rwm25tsd')
      setDashboardFactory(factoryNode.code)
      setSelectedLevel(2)
    } else if (level === 2) {
      // Nivel 3: Equipo/Componente - Mostrar dashboard
      const factoryNode = path[0]
      const lineNode = path[1]
      setFactory(factoryNode.code)
      setProdLine(lineNode.code)
      // Establecer el dashboard UID según el código del nodo del Nivel 3
      const nodeCode = node.code
      const dashboardUid = DASHBOARD_UIDS[nodeCode] || '6cb7e678-82e6-405b-8680-cc2bb4718334' // Default
      setDashboardUid(dashboardUid)
      // Establecer el factory para el dashboard según el código del nodo
      if (nodeCode === '8_Winders') {
        setDashboardFactory('CXC_L8K')
      } else {
        setDashboardFactory(factoryNode.code)
      }
      setSelectedLevel(3)
    } else if (level >= 3) {
      // Nivel 4+: Equipo hijo - Usar el dashboard del padre (Nivel 3)
      const factoryNode = path[0]
      const lineNode = path[1]
      const level3Node = path[2] // Nodo del Nivel 3
      setFactory(factoryNode.code)
      setProdLine(lineNode.code)
      // Establecer el dashboard UID según el código del nodo del Nivel 3 (padre)
      if (level3Node) {
        const nodeCode = level3Node.code
        // Caso especial: Si estamos en nivel 4 (level === 3) y el nivel 3 es "2_Dosing" o "3_Extrusion", usar dashboard específico
        if (level === 3 && nodeCode === '2_Dosing') {
          setDashboardUid('dosing-system-standalone')
          setMachine(node.code) // El nodo seleccionado (nivel 4) es la máquina
          // Buscar el extruder en los hermanos del nivel 3 o en el path
          // Por ahora, necesito saber cómo obtenerlo. ¿Está en algún lugar específico del path?
          // Por ejemplo, si hay un nodo "3_Extrusion" en el mismo nivel 3:
          const lineNodeChildren = lineNode.children || []
          const extrusionNode = lineNodeChildren.find(child => child.code === '3_Extrusion')
          if (extrusionNode) {
            // Buscar el primer hijo de Extrusion que tenga código como "Extr_A"
            const extruderChild = extrusionNode.children?.find(child => child.code?.startsWith('Extr_'))
            if (extruderChild) {
              setExtruder(extruderChild.code)
            }
          }
        } else if (level === 3 && nodeCode === '3_Extrusion') {
          setDashboardUid('4cd1d21f-807c-4e6e-a4d1-7335c2d8bfb4')
        }else if (level === 3 && nodeCode === '8_Winders') {
          setDashboardUid('d5328cf4-d78d-4f43-8385-2d113fea4e98')
        }
         else {
          const dashboardUid = DASHBOARD_UIDS[nodeCode] || '6cb7e678-82e6-405b-8680-cc2bb4718334' // Default
          setDashboardUid(dashboardUid)
        }
        // Establecer el factory para el dashboard según el código del nodo del Nivel 3
        if (nodeCode === '8_Winders') {
          setDashboardFactory('CXC_L8K')
        } else {
          setDashboardFactory(factoryNode.code)
        }
      }
      setSelectedLevel(3)
    } else {
      setSelectedLevel(null)
    }
  }

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev)
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId)
      } else {
        newSet.add(nodeId)
      }
      return newSet
    })
  }

  const expandAll = () => {
    const nodeIds = new Set()
    Object.values(hierarchies).forEach(factory => {
      const collectIds = (n) => {
        nodeIds.add(n.id)
        if (n.children) {
          n.children.forEach(child => collectIds(child))
        }
      }
      collectIds(factory)
    })
    setExpandedNodes(nodeIds)
  }

  const collapseAll = () => {
    setExpandedNodes(new Set())
  }

  const getLevelColor = (level) => {
    switch (level) {
      case 0: return 'text-blue-400'
      case 1: return 'text-green-400'
      case 2: return 'text-yellow-400'
      default: return 'text-gray-300'
    }
  }

  // Filtrar nodos según búsqueda
  const filterNode = (node) => {
    if (!node) return null
    
    if (searchQuery) {
      const matchesSearch = 
        node.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        node.location?.toLowerCase().includes(searchQuery.toLowerCase())
      
      if (matchesSearch) {
        return { ...node, children: node.children?.map(filterNode).filter(Boolean) }
      }
      
      if (node.children && node.children.length > 0) {
        const filteredChildren = node.children.map(filterNode).filter(Boolean)
        if (filteredChildren.length > 0) {
          return { ...node, children: filteredChildren }
        }
      }
      
      return null
    }
    
    return {
      ...node,
      children: node.children?.map(filterNode).filter(Boolean)
    }
  }

  const renderTreeNode = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0
    const isExpanded = expandedNodes.has(node.id)
    const isSelected = selectedNode?.id === node.id
    const indent = level * 16
    const basePadding = 5

    return (
      <div key={node.id}>
        <div 
          className={`flex items-center gap-1 px-1 py-0.5 hover:bg-gray-800/50 rounded cursor-pointer group transition-colors ${
            isSelected ? 'bg-blue-500/20 border-l-2 border-blue-500' : ''
          }`}
          style={{ paddingLeft: `${basePadding + indent}px` }}
          onClick={() => {
            if (hasChildren) {
              toggleNode(node.id)
            }
            handleNodeSelect(node)
          }}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown size={12} className="text-gray-400 flex-shrink-0" />
            ) : (
              <ChevronRight size={12} className="text-gray-400 flex-shrink-0" />
            )
          ) : (
            <div className="w-3 flex-shrink-0" />
          )}
          
          <span 
            className={`flex-1 min-w-0 truncate text-xs transition-colors ${
              isSelected 
                ? 'text-blue-400 font-semibold' 
                : getLevelColor(level)
            }`}
          >
            {node.code}
          </span>
        </div>
        
        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    )
  }

  const renderFullTree = () => {
    const factoryList = Object.keys(hierarchies).sort()
    
    return (
      <div className="space-y-0.5">
        {factoryList.map(factoryCode => {
          const factory = hierarchies[factoryCode]
          const filteredFactory = filterNode(factory)
          if (filteredFactory) {
            return renderTreeNode(filteredFactory, 0)
          }
          return null
        })}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin text-blue-500" size={48} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">{error}</p>
          <button
            onClick={() => navigate('/parametros-procesos')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white"
          >
            Volver al Inicio
          </button>
        </div>
      </div>
    )
  }

  if (Object.keys(hierarchies).length === 0 && !loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center text-gray-500">
          <p className="text-lg mb-4">No hay datos disponibles</p>
          <button
            onClick={() => loadHierarchies()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white"
          >
            Recargar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <BarChart3 size={32} />
          {t.activos?.dashboards?.title || t.navigation?.dashboards || 'Dashboards'}
        </h1>
        <p className="text-gray-400 mt-2">
          Navega por el árbol de activos para ver dashboards
        </p>
      </div>

      {/* Contenido principal */}
      <div className="flex h-[calc(100vh-200px)] gap-4 flex-1 min-h-0 px-2">
        {/* Sidebar Izquierdo: Árbol de Jerarquía */}
        <div className={`flex-shrink-0 bg-gray-900/50 rounded-lg border border-gray-800 overflow-hidden flex flex-col ${
          !isMobile ? 'w-64' : 'w-full lg:w-72'
        }`}>
          {/* Header del Sidebar */}
          <div className="px-3 py-2 border-b border-gray-800 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-white flex items-center gap-1.5">
                <Factory size={16} />
                {t.activos?.dashboards?.tree || 'Árbol de Activos'}
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {selectedLevel === null && 'Selecciona un activo'}
                {selectedLevel === 0 && 'Nivel 0: Fábrica seleccionada'}
                {selectedLevel === 1 && 'Nivel 1: Línea seleccionada'}
                {selectedLevel === 2 && 'Nivel 2: Dashboard disponible'}
                {selectedLevel === 3 && 'Nivel 3: Dashboard disponible'}
              </p>
            </div>
            <div className="flex gap-1">
              <button
                onClick={expandAll}
                className="p-1 hover:bg-gray-800 rounded text-gray-400 hover:text-white transition-colors"
                title="Expandir todo"
              >
                <ChevronDown size={14} />
              </button>
              <button
                onClick={collapseAll}
                className="p-1 hover:bg-gray-800 rounded text-gray-400 hover:text-white transition-colors"
                title="Colapsar todo"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>

          {/* Búsqueda en Sidebar */}
          <div className="px-2 py-1.5 border-b border-gray-800">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-7 pr-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-white text-xs placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          {/* Árbol de Jerarquía */}
          <div className="flex-1 overflow-y-auto py-1">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="animate-spin text-blue-500" size={24} />
              </div>
            ) : Object.keys(hierarchies).length > 0 ? (
              renderFullTree()
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                No hay datos disponibles
              </div>
            )}
          </div>
        </div>

        {/* Panel Derecho: Dashboard */}
        {!isMobile && (
          <div className="flex-1 min-w-0">
            {(selectedLevel === 2 || selectedLevel === 3) && dashboardFactory && prodLine ? (
              <Card className="h-full flex flex-col min-h-0 w-full">
                <DashboardViewer
                  dashboardUid={dashboardUid}
                  factory={dashboardFactory}
                  prodLine={prodLine}
                  assetId={selectedNode?.id}
                  extruder={extruder}
                  machine={machine}
                />
              </Card>
            ) : (
              <Card className="h-full flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <Info size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="text-lg">
                    {selectedLevel === null && 'Selecciona un activo del árbol'}
                    {selectedLevel === 0 && 'Selecciona una línea (Nivel 1)'}
                    {selectedLevel === 1 && 'Selecciona un equipo (Nivel 3) para ver el dashboard'}
                  </p>
                  {selectedLevel !== null && (
                    <p className="text-sm mt-2 text-gray-400">
                      {factory && <span>Fábrica: {factory}</span>}
                      {prodLine && <span className="ml-2">Línea: {prodLine}</span>}
                    </p>
                  )}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Modal de Dashboard (Mobile) */}
        {isMobile && (selectedLevel === 2 || selectedLevel === 3) && dashboardFactory && prodLine && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm">
            <div className="h-full">
              <Card className="h-full flex flex-col min-h-0 w-full">
                <DashboardViewer
                  dashboardUid={dashboardUid}
                  factory={dashboardFactory}
                  prodLine={prodLine}
                  assetId={selectedNode?.id}
                />
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ParametrosProcesos